import type { Ref } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { Trans } from '@lingui/react/macro'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useThrottle } from '@jotta/hooks'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { Box } from '@jotta/ui/Box'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import SvgX from '@jotta/ui/SvgX'
import type { ThemeUIStyleObject } from '@jotta/ui/themeui'
import { SpinnerThemeUI, TextThemeUI } from '@jotta/ui/themeui'
import { getBrandZIndex } from '@jotta/ui/zIndex'

import type {
  CommentResponseWithImage,
  CommentWithImage,
} from '../../store/CommentQuery'

import { CommentInput } from './CommentInput'
import { CommentItem } from './CommentItem'
import { CommentItemStore, CommentsContext } from './CommentsContext'
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog'
import { TextareaStore } from './TextArea'

const CommentSectionHeader: React.FC<{
  darkMode: boolean
  onClose?: () => void
}> = ({ darkMode, onClose }) => {
  return (
    <Flex
      sx={{
        height: 'var(--header-height)',
        borderBottom: '1px solid',
        borderBottomColor: darkMode ? '#191919' : 'border',
        bg: darkMode ? 'photoInfoBgColor' : 'white',
        zIndex: getBrandZIndex('dropdown'),
        alignItems: 'center',
      }}
    >
      <Button
        onClick={onClose}
        sx={{
          background: 'none',
          border: 'none',
          px: 3,
          opacity: 0.6,
          height: '100%',
          color: 'inherit',
          borderRadius: 2,
          '&:hover': {
            opacity: 0.9,
          },
          '&:focus-visible': {
            boxShadow: 'inset 0 0 0 2px var(--color-focus)',
          },
          span: {
            display: 'flex',
          },
        }}
      >
        <BrandIcon icon={SvgX} sx={{ fontSize: 4 }} />
      </Button>
      <TextThemeUI
        as="span"
        sx={{
          lineHeight: '60px',
          fontSize: 3,
          verticalAlign: 'top',
          color: 'inherit',
        }}
      >
        <Trans>Comments</Trans>
      </TextThemeUI>
    </Flex>
  )
}

const EditOverlay: React.FC<{ show?: boolean }> = ({ show = false }) => {
  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            sx={{
              pointerEvents: 'none',
              backgroundColor: 'black',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

const CommentSpinner: React.FC<{ show?: boolean }> = ({ show = false }) =>
  show ? (
    <Box sx={{ margin: 'auto' }}>
      <SpinnerThemeUI sx={{ mt: 3, display: 'block' }} />
    </Box>
  ) : null

const CommentList: React.FC<{
  show?: boolean | number
  comments: CommentWithImage[]
  textStore: TextareaStore
  album?: PhotosApi.Album
  darkMode: boolean
}> = ({ show = false, comments, textStore, album, darkMode }) => {
  if (!show) {
    return null
  }

  if (!comments.length) {
    return (
      <span sx={{ opacity: 0.6, margin: 4 }}>
        <Trans>There are no comments yet.</Trans>
      </span>
    )
  }

  return (
    <LayoutGroup>
      <AnimatePresence initial={false}>
        {comments.map(comment => (
          <CommentItem
            textStore={textStore}
            album={album}
            {...comment}
            key={comment.id}
            darkMode={darkMode}
          />
        ))}
      </AnimatePresence>
    </LayoutGroup>
  )
}

// Start list scrolled to bottom and keep position if scrollTop == 0 or at bottom
function useScrollPreserve<E extends HTMLElement>(observed: unknown) {
  const elementRef = useRef<E>(null)
  const prevScrollHeightRef = useRef(0)
  const scrollFromBottomRef = useRef(0)

  scrollFromBottomRef.current = elementRef.current
    ? elementRef.current.scrollHeight -
      elementRef.current.clientHeight -
      elementRef.current.scrollTop
    : 0

  useEffect(() => {
    if (!elementRef.current) {
      return
    }

    if (prevScrollHeightRef.current !== elementRef.current.scrollHeight) {
      // If scrolled to bottom when new contend added, keep it scrolled to bottom
      if (scrollFromBottomRef.current === 0) {
        elementRef.current.scrollTop =
          elementRef.current.scrollHeight - elementRef.current.clientHeight
      }
      // If scrolled to top when new content was added preserve scroll position
      else if (elementRef.current.scrollTop === 0) {
        elementRef.current.scrollTop =
          elementRef.current.scrollHeight - prevScrollHeightRef.current
      }
      prevScrollHeightRef.current = elementRef.current.scrollHeight
    }
  }, [prevScrollHeightRef, elementRef, observed])

  return elementRef
}

const CommentSection = observer<{
  pages: CommentResponseWithImage[] | undefined
  hasNextPage: boolean
  onClose?: (open?: boolean) => void
  onAddComment?: (text: string) => void
  onEdit?: (contextKey: string, commentId: string, text: string) => void
  onReply?: (contextKey: string, commentId: string, text: string) => void
  onDelete?: (contextKey: string, commentId: string) => void
  onFetchNextPage?: () => void
  darkMode?: boolean
  isFetchingNextPage: boolean
  scrollToCommentId?: string
  album?: PhotosApi.Album
  ref: Ref<HTMLDivElement>
}>(function CommentSection({
  pages = [],
  onFetchNextPage,
  hasNextPage,
  onClose,
  onAddComment,
  onEdit,
  onReply,
  onDelete,
  darkMode = false,
  isFetchingNextPage,
  scrollToCommentId,
  album,
  ref,
}) {
  type CommentInfo = { commentId: string; contextKey: string }

  const [editMode, setEditMode] = useState<CommentInfo | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CommentInfo | null>(null)
  const textStore = useMemo(() => new TextareaStore(''), [])
  const authenticated = useAuthenticated()

  const listRef = useScrollPreserve<HTMLDivElement>(pages)

  const loadMore = useCallback(() => {
    if (
      !onFetchNextPage ||
      !hasNextPage ||
      isFetchingNextPage ||
      !listRef.current
    ) {
      return
    }

    const el = listRef.current

    if (el.scrollTop <= 400) {
      onFetchNextPage()
    }
  }, [onFetchNextPage, hasNextPage, isFetchingNextPage, listRef])

  // Load more whenever function (its deps) changes
  useEffect(() => {
    loadMore()
  }, [loadMore])

  const handleScroll = useThrottle(loadMore, 100)

  const onSubmit = (text: string) => {
    if (!text) {
      return
    }
    if (editMode) {
      onEdit?.(editMode.contextKey, editMode.commentId, text.trim())
      setEditMode(null)
    } else {
      if (listRef.current) {
        // Scroll to bottom
        const el = listRef.current
        el.scrollTop = el.scrollHeight - el.clientHeight
      }
      onAddComment?.(text.trim())
    }
  }

  const normalStyle: ThemeUIStyleObject = {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    wordBreak: 'break-word',
    display: 'flex',
    flexDirection: 'column',
  }

  const editStyle: ThemeUIStyleObject = {
    ...{ normalStyle },
    pointerEvents: 'none',
    userSelect: 'none',
  }

  const enableEditMode = (
    contextKey: string,
    commentId: string,
    text: string,
  ) => {
    setEditMode({ contextKey, commentId })
    textStore.text = text
  }

  const doConfirmDelete = () => {
    if (!confirmDelete) {
      return
    }
    if (onDelete) {
      onDelete(confirmDelete.contextKey, confirmDelete.commentId)
    }
    setConfirmDelete(null)
  }

  const comments = useMemo(
    () =>
      pages
        .map(page => page.commentsList)
        .reverse()
        .flat(),
    [pages],
  )

  const commentItemStore = useMemo(() => new CommentItemStore(), [])

  useEffect(() => {
    if (!editMode) {
      return
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditMode(null)
        e.preventDefault()
      }
    }

    document.addEventListener('keydown', handleEscape, true)

    return () => {
      document.removeEventListener('keydown', handleEscape, true)
    }
  }, [editMode])

  return (
    <CommentsContext.Provider
      value={{
        darkMode,
        scrollToCommentId,
        onEdit: enableEditMode,
        onDelete(contextKey, commentId) {
          setConfirmDelete({ contextKey, commentId })
        },
        onReply(contextKey, commentId, text) {
          if (onReply) {
            onReply(contextKey, commentId, text)
          }
        },
        commentItemStore,
      }}
    >
      <Box
        ref={ref}
        sx={{
          flex: '1 1 auto',
          height: '100%',
          backgroundColor: darkMode ? 'photoInfoBgColor' : 'white',
          color: darkMode ? 'white' : '#333', // TODO: Fix hardcoded color
          ...(darkMode ? { '--_color-focus': 'var(--color-focus-dark)' } : {}),
        }}
      >
        <Flex column sx={{ height: '100%' }}>
          <CommentSectionHeader onClose={onClose} darkMode={darkMode} />
          <Box
            sx={{
              flex: '1 1 auto',
              //position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              ref={listRef}
              sx={editMode ? editStyle : normalStyle}
              onScroll={handleScroll}
            >
              <CommentList
                show={!isFetchingNextPage || comments.length}
                textStore={textStore}
                comments={comments}
                album={album}
                darkMode={darkMode}
              />
              <CommentSpinner show={isFetchingNextPage} />
            </Box>
            <EditOverlay show={Boolean(editMode)} />
          </Box>
          <CommentInput
            textStore={textStore}
            onSubmit={onSubmit}
            notLoginUser={authenticated === 'FALSE'}
            darkMode={darkMode}
          />
        </Flex>

        <ConfirmDeleteDialog
          onClose={() => {
            setConfirmDelete(null)
          }}
          onConfirm={doConfirmDelete}
          open={Boolean(confirmDelete)}
        />
      </Box>
    </CommentsContext.Provider>
  )
})

export default CommentSection
